import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Database, Users, Calendar, CreditCard, Heart, FileText,
  ChevronLeft, Search, Shield, Loader2, User, Mail, MapPin,
  Clock, Euro, TrendingUp, Filter, RefreshCw, Download
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDatabasePage() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { t, lang } = useTranslation();

  const [activeTab, setActiveTab] = useState('stats');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const labels = {
    stats: { fr: 'Statistiques', en: 'Statistics', nl: 'Statistieken' },
    users: { fr: 'Utilisateurs', en: 'Users', nl: 'Gebruikers' },
    sessions: { fr: 'Séances', en: 'Sessions', nl: 'Sessies' },
    bookings: { fr: 'Réservations', en: 'Bookings', nl: 'Boekingen' },
    title: { fr: 'Base de données', en: 'Database', nl: 'Database' },
    subtitle: { fr: 'Visualisez et gérez toutes les données Motivya', en: 'View and manage all Motivya data', nl: 'Bekijk en beheer alle Motivya-gegevens' },
    totalUsers: { fr: 'Utilisateurs totaux', en: 'Total users', nl: 'Totaal gebruikers' },
    coaches: { fr: 'Coachs', en: 'Coaches', nl: 'Coaches' },
    practitioners: { fr: 'Pratiquants', en: 'Practitioners', nl: 'Beoefenaars' },
    totalSessions: { fr: 'Séances créées', en: 'Sessions created', nl: 'Sessies aangemaakt' },
    totalBookings: { fr: 'Réservations', en: 'Bookings', nl: 'Boekingen' },
    applications: { fr: 'Candidatures', en: 'Applications', nl: 'Kandidaturen' },
    favorites: { fr: 'Favoris', en: 'Favourites', nl: 'Favorieten' },
    revenue: { fr: 'Revenu total', en: 'Total revenue', nl: 'Totale omzet' },
    search: { fr: 'Rechercher...', en: 'Search...', nl: 'Zoeken...' },
    all: { fr: 'Tous', en: 'All', nl: 'Alle' },
    name: { fr: 'Nom', en: 'Name', nl: 'Naam' },
    email: { fr: 'Email', en: 'Email', nl: 'E-mail' },
    role: { fr: 'Rôle', en: 'Role', nl: 'Rol' },
    city: { fr: 'Ville', en: 'City', nl: 'Stad' },
    registered: { fr: 'Inscrit le', en: 'Registered', nl: 'Geregistreerd' },
    bookingsCount: { fr: 'Réservations', en: 'Bookings', nl: 'Boekingen' },
    coach: { fr: 'Coach', en: 'Coach', nl: 'Coach' },
    date: { fr: 'Date', en: 'Date', nl: 'Datum' },
    time: { fr: 'Heure', en: 'Time', nl: 'Tijd' },
    location: { fr: 'Lieu', en: 'Location', nl: 'Locatie' },
    participants: { fr: 'Participants', en: 'Participants', nl: 'Deelnemers' },
    price: { fr: 'Prix', en: 'Price', nl: 'Prijs' },
    status: { fr: 'Statut', en: 'Status', nl: 'Status' },
    amount: { fr: 'Montant', en: 'Amount', nl: 'Bedrag' },
    session: { fr: 'Séance', en: 'Session', nl: 'Sessie' },
    user: { fr: 'Utilisateur', en: 'User', nl: 'Gebruiker' },
    loginRequired: { fr: 'Accès restreint', en: 'Restricted access', nl: 'Beperkte toegang' },
    loginDesc: { fr: 'Connectez-vous en tant qu\'admin pour accéder à la base de données', en: 'Sign in as admin to access the database', nl: 'Log in als admin om toegang te krijgen tot de database' },
    login: { fr: 'Se connecter', en: 'Sign in', nl: 'Inloggen' },
    refresh: { fr: 'Actualiser', en: 'Refresh', nl: 'Vernieuwen' },
    confirmed: { fr: 'Confirmé', en: 'Confirmed', nl: 'Bevestigd' },
    pending: { fr: 'En attente', en: 'Pending', nl: 'In afwachting' },
    cancelled: { fr: 'Annulé', en: 'Cancelled', nl: 'Geannuleerd' },
    noData: { fr: 'Aucune donnée', en: 'No data', nl: 'Geen gegevens' },
    admin: { fr: 'Admin', en: 'Admin', nl: 'Admin' },
    exportCsv: { fr: 'Exporter CSV', en: 'Export CSV', nl: 'Exporteer CSV' },
  };

  const l = (key) => labels[key]?.[lang] || labels[key]?.['fr'] || key;

  const exportToCsv = (data, filename, columns) => {
    if (!data || data.length === 0) return;
    const headers = columns.map(c => c.label).join(',');
    const rows = data.map(row => columns.map(c => {
      const val = String(row[c.key] ?? '').replace(/,/g, ' ').replace(/\n/g, ' ');
      return `"${val}"`;
    }).join(',')).join('\n');
    const csv = `${headers}\n${rows}`;
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${filename}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (isAuthenticated) fetchStats();
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (activeTab === 'users') fetchUsers();
    else if (activeTab === 'sessions') fetchSessions();
    else if (activeTab === 'bookings') fetchBookings();
  }, [activeTab, roleFilter]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/db/stats`, { credentials: 'include' });
      if (res.ok) setStats(await res.json());
      else if (res.status === 403) toast.error('Admin access required');
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const url = roleFilter !== 'all' 
        ? `${API_URL}/api/admin/db/users?role=${roleFilter}&page_size=500` 
        : `${API_URL}/api/admin/db/users?page_size=500`;
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        // Handle paginated response
        setUsers(data.items || data);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/db/sessions?page_size=500`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        // Handle paginated response
        setSessions(data.items || data);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/db/bookings?page_size=500`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        // Handle paginated response
        setBookings(data.items || data);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const refreshAll = () => {
    fetchStats();
    if (activeTab === 'users') fetchUsers();
    else if (activeTab === 'sessions') fetchSessions();
    else if (activeTab === 'bookings') fetchBookings();
  };

  const formatDate = (d) => {
    if (!d) return 'N/A';
    const date = new Date(d);
    const locale = lang === 'nl' ? 'nl-BE' : lang === 'en' ? 'en-GB' : 'fr-FR';
    return date.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const filterBySearch = (items, fields) => {
    if (!searchTerm) return items;
    const term = searchTerm.toLowerCase();
    return items.filter(item => fields.some(f => String(item[f] || '').toLowerCase().includes(term)));
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Database className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">{l('loginRequired')}</h2>
          <p className="text-muted-foreground mb-4">{l('loginDesc')}</p>
          <Link to="/login"><Button className="btn-primary">{l('login')}</Button></Link>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'stats', icon: TrendingUp, label: l('stats') },
    { id: 'users', icon: Users, label: l('users') },
    { id: 'sessions', icon: Calendar, label: l('sessions') },
    { id: 'bookings', icon: CreditCard, label: l('bookings') },
  ];

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate(-1)} className="text-muted-foreground hover:text-foreground">
                <ChevronLeft className="w-6 h-6" />
              </button>
              <div className="flex items-center gap-2">
                <Database className="w-5 h-5 text-primary" />
                <span className="font-bold uppercase">{l('title')}</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={refreshAll} data-testid="refresh-db">
                <RefreshCw className="w-4 h-4 mr-2" />
                {l('refresh')}
              </Button>
              <div className="text-sm text-muted-foreground">
                <Shield className="w-4 h-4 inline mr-1" />
                {user?.email}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold mb-1" data-testid="admin-db-title">{l('title')}</h1>
          <p className="text-muted-foreground">{l('subtitle')}</p>
        </div>

        <div className="flex gap-2 mb-8 border-b border-border pb-4 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setSearchTerm(''); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground hover:text-foreground border border-border'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : (
          <>
            {activeTab === 'stats' && stats && <StatsView stats={stats} l={l} />}
            {activeTab === 'users' && (
              <UsersView
                users={filterBySearch(users, ['name', 'email', 'city'])}
                roleFilter={roleFilter}
                setRoleFilter={setRoleFilter}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                formatDate={formatDate}
                l={l}
                exportToCsv={exportToCsv}
              />
            )}
            {activeTab === 'sessions' && (
              <SessionsView
                sessions={filterBySearch(sessions, ['title', 'coach_name', 'location', 'category'])}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                formatDate={formatDate}
                l={l}
                exportToCsv={exportToCsv}
              />
            )}
            {activeTab === 'bookings' && (
              <BookingsView
                bookings={filterBySearch(bookings, ['user_name', 'user_email', 'session_title'])}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                formatDate={formatDate}
                l={l}
                exportToCsv={exportToCsv}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function StatsView({ stats, l }) {
  const cards = [
    { label: l('totalUsers'), value: stats.users, icon: Users, color: 'text-blue-400', bg: 'bg-blue-500/10' },
    { label: l('coaches'), value: stats.coaches, icon: User, color: 'text-green-400', bg: 'bg-green-500/10' },
    { label: l('practitioners'), value: stats.practitioners, icon: Users, color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
    { label: l('totalSessions'), value: stats.sessions, icon: Calendar, color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
    { label: l('totalBookings'), value: stats.bookings, icon: CreditCard, color: 'text-purple-400', bg: 'bg-purple-500/10' },
    { label: l('applications'), value: stats.applications, icon: FileText, color: 'text-orange-400', bg: 'bg-orange-500/10' },
    { label: l('favorites'), value: stats.favorites, icon: Heart, color: 'text-red-400', bg: 'bg-red-500/10' },
    { label: l('revenue'), value: `${stats.total_revenue}€`, icon: Euro, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="stats-grid">
      {cards.map((card, i) => (
        <div key={i} className={`${card.bg} border border-border rounded-xl p-5`}>
          <div className="flex items-center gap-2 mb-2">
            <card.icon className={`w-5 h-5 ${card.color}`} />
            <span className="text-sm text-muted-foreground">{card.label}</span>
          </div>
          <div className={`text-3xl font-bold ${card.color}`}>{card.value}</div>
        </div>
      ))}
    </div>
  );
}

function SearchBar({ searchTerm, setSearchTerm, l }) {
  return (
    <div className="relative mb-4">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
      <Input
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder={l('search')}
        className="pl-10"
        data-testid="db-search"
      />
    </div>
  );
}

function UsersView({ users, roleFilter, setRoleFilter, searchTerm, setSearchTerm, formatDate, l, exportToCsv }) {
  const roles = ['all', 'user', 'coach', 'admin'];

  const handleExport = () => {
    exportToCsv(users, 'motivya_users', [
      { key: 'name', label: l('name') },
      { key: 'email', label: l('email') },
      { key: 'role', label: l('role') },
      { key: 'city', label: l('city') },
      { key: 'phone', label: 'Phone' },
      { key: 'created_at', label: l('registered') },
      { key: 'bookings_count', label: l('bookingsCount') },
    ]);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-4 mb-4">
        <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} l={l} />
        <div className="flex gap-2 shrink-0">
          {roles.map(r => (
            <button
              key={r}
              onClick={() => setRoleFilter(r)}
              className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                roleFilter === r ? 'bg-primary text-primary-foreground' : 'bg-card border border-border text-muted-foreground'
              }`}
              data-testid={`role-filter-${r}`}
            >
              {r === 'all' ? l('all') : r === 'user' ? l('practitioners') : r === 'coach' ? l('coaches') : l('admin')}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-muted-foreground">{users.length} {l('users').toLowerCase()}</div>
        <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-users-csv">
          <Download className="w-4 h-4 mr-2" />
          {l('exportCsv')}
        </Button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="py-3 px-4">{l('name')}</th>
              <th className="py-3 px-4">{l('email')}</th>
              <th className="py-3 px-4">{l('role')}</th>
              <th className="py-3 px-4">{l('city')}</th>
              <th className="py-3 px-4">{l('registered')}</th>
              <th className="py-3 px-4">{l('bookingsCount')}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u, i) => (
              <tr key={i} className="border-b border-border/50 hover:bg-card/50 transition-colors">
                <td className="py-3 px-4 font-medium">{u.name || 'N/A'}</td>
                <td className="py-3 px-4 text-muted-foreground">{u.email}</td>
                <td className="py-3 px-4">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    u.role === 'admin' ? 'bg-purple-500/20 text-purple-400' :
                    u.role === 'coach' ? 'bg-green-500/20 text-green-400' :
                    'bg-blue-500/20 text-blue-400'
                  }`}>{u.role}</span>
                </td>
                <td className="py-3 px-4 text-muted-foreground">{u.city || '-'}</td>
                <td className="py-3 px-4 text-muted-foreground">{formatDate(u.created_at)}</td>
                <td className="py-3 px-4 text-center">{u.bookings_count || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {users.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">{l('noData')}</div>
      )}
    </div>
  );
}

function SessionsView({ sessions, searchTerm, setSearchTerm, formatDate, l, exportToCsv }) {
  const handleExport = () => {
    exportToCsv(sessions, 'motivya_sessions', [
      { key: 'title', label: 'Titre' },
      { key: 'coach_name', label: l('coach') },
      { key: 'coach_email', label: 'Coach Email' },
      { key: 'date', label: l('date') },
      { key: 'start_time', label: l('time') },
      { key: 'location', label: l('location') },
      { key: 'category', label: 'Category' },
      { key: 'current_participants', label: l('participants') },
      { key: 'max_participants', label: 'Max' },
      { key: 'price', label: l('price') },
      { key: 'status', label: l('status') },
    ]);
  };

  return (
    <div>
      <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} l={l} />
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-muted-foreground">{sessions.length} {l('sessions').toLowerCase()}</div>
        <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-sessions-csv">
          <Download className="w-4 h-4 mr-2" />
          {l('exportCsv')}
        </Button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="sessions-table">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="py-3 px-4">Titre</th>
              <th className="py-3 px-4">{l('coach')}</th>
              <th className="py-3 px-4">{l('date')}</th>
              <th className="py-3 px-4">{l('location')}</th>
              <th className="py-3 px-4">{l('participants')}</th>
              <th className="py-3 px-4">{l('price')}</th>
              <th className="py-3 px-4">{l('status')}</th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((s, i) => (
              <tr key={i} className="border-b border-border/50 hover:bg-card/50 transition-colors">
                <td className="py-3 px-4 font-medium">
                  <Link to={`/session/${s.session_id}`} className="hover:text-primary transition-colors">
                    {s.title}
                  </Link>
                </td>
                <td className="py-3 px-4 text-muted-foreground">{s.coach_name}</td>
                <td className="py-3 px-4 text-muted-foreground">{formatDate(s.date)}</td>
                <td className="py-3 px-4 text-muted-foreground max-w-[150px] truncate">{s.location}</td>
                <td className="py-3 px-4">{s.current_participants}/{s.max_participants}</td>
                <td className="py-3 px-4 text-primary font-medium">{s.price}€</td>
                <td className="py-3 px-4">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    s.status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                    s.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                    'bg-yellow-500/20 text-yellow-400'
                  }`}>{s.status === 'confirmed' ? l('confirmed') : s.status === 'cancelled' ? l('cancelled') : l('pending')}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sessions.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">{l('noData')}</div>
      )}
    </div>
  );
}

function BookingsView({ bookings, searchTerm, setSearchTerm, formatDate, l, exportToCsv }) {
  const handleExport = () => {
    exportToCsv(bookings, 'motivya_bookings', [
      { key: 'user_name', label: l('user') },
      { key: 'user_email', label: l('email') },
      { key: 'session_title', label: l('session') },
      { key: 'session_date', label: l('date') },
      { key: 'amount', label: l('amount') },
      { key: 'status', label: l('status') },
      { key: 'created_at', label: l('registered') },
    ]);
  };

  return (
    <div>
      <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} l={l} />
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-muted-foreground">{bookings.length} {l('bookings').toLowerCase()}</div>
        <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-bookings-csv">
          <Download className="w-4 h-4 mr-2" />
          {l('exportCsv')}
        </Button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="bookings-table">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="py-3 px-4">{l('user')}</th>
              <th className="py-3 px-4">{l('session')}</th>
              <th className="py-3 px-4">{l('date')}</th>
              <th className="py-3 px-4">{l('amount')}</th>
              <th className="py-3 px-4">{l('status')}</th>
            </tr>
          </thead>
          <tbody>
            {bookings.map((b, i) => (
              <tr key={i} className="border-b border-border/50 hover:bg-card/50 transition-colors">
                <td className="py-3 px-4">
                  <div className="font-medium">{b.user_name}</div>
                  <div className="text-xs text-muted-foreground">{b.user_email}</div>
                </td>
                <td className="py-3 px-4 font-medium">{b.session_title}</td>
                <td className="py-3 px-4 text-muted-foreground">{formatDate(b.session_date)}</td>
                <td className="py-3 px-4 text-primary font-medium">{b.amount || 0}€</td>
                <td className="py-3 px-4">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    b.status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                    b.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                    'bg-yellow-500/20 text-yellow-400'
                  }`}>{b.status === 'confirmed' ? l('confirmed') : b.status === 'cancelled' ? l('cancelled') : l('pending')}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {bookings.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">{l('noData')}</div>
      )}
    </div>
  );
}
